import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client.js";
import { PageHeader, Panel, Spinner } from "../components/Panels.js";

interface StaffRow {
  id: string;
  email: string;
  full_name: string;
  role: string;
  business_unit_id: number | null;
  business_unit_code: string | null;
  business_unit_name: string | null;
  manager_id: string | null;
  manager_name: string | null;
  is_executive: number;
  reports_to_ceo: number;
  timezone: string;
  daily_checkin_time: string;
  status: string;
  pto_until: string | null;
  opted_out: number;
}

interface BU {
  id: number;
  code: string;
  name: string;
  active: number;
}

const STATUSES = ["active", "pto", "inactive", "on_leave"] as const;
const TIMEZONES = ["Australia/Sydney", "Asia/Manila"];

export function StaffPage() {
  const [staff, setStaff] = useState<StaffRow[] | null>(null);
  const [bus, setBus] = useState<BU[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<StaffRow>>({});
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = () => {
    api
      .get<{ staff: StaffRow[] }>("/staff")
      .then((r) => setStaff(r.staff))
      .catch((e: Error) => setError(e.message));
  };

  useEffect(() => {
    refresh();
    api.get<{ business_units: BU[] }>("/business-units").then((r) => setBus(r.business_units));
  }, []);

  const managers = useMemo(
    () => staff?.filter((s) => s.is_executive === 1 || s.reports_to_ceo === 1) ?? [],
    [staff],
  );

  if (error) return <div className="p-6 text-sm text-sentiment-overwhelmed">{error}</div>;
  if (!staff) return <div className="p-6"><Spinner /></div>;

  const startEdit = (row: StaffRow) => {
    setEditing(row.id);
    setDraft({ ...row });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.patch(`/staff/${editing}`, {
        full_name: draft.full_name,
        role: draft.role,
        business_unit_id: draft.business_unit_id,
        manager_id: draft.manager_id,
        is_executive: draft.is_executive === 1,
        reports_to_ceo: draft.reports_to_ceo === 1,
        timezone: draft.timezone,
        daily_checkin_time: draft.daily_checkin_time,
        status: draft.status,
        pto_until: draft.pto_until,
        opted_out: draft.opted_out === 1,
      });
      setEditing(null);
      setDraft({});
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      await api.post("/admin/sync-staff");
      refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Staff & org"
        subtitle="Edit reporting lines, working hours, and check-in eligibility."
      />
      <div className="px-6">
        <Panel
          title={`${staff.length} staff`}
          action={
            <button
              type="button"
              onClick={sync}
              disabled={syncing}
              className="text-xs px-3 py-1 rounded-md border border-bg-line2 text-ink hover:bg-bg-surface disabled:opacity-50"
            >
              {syncing ? "Syncing…" : "Sync from Slack"}
            </button>
          }
        >
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-ink-dimmer">
              <tr className="text-left">
                <th className="py-2 font-normal">Name</th>
                <th className="py-2 font-normal">Role</th>
                <th className="py-2 font-normal">BU</th>
                <th className="py-2 font-normal">Manager</th>
                <th className="py-2 font-normal">TZ / time</th>
                <th className="py-2 font-normal">Status</th>
                <th className="py-2 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-bg-line">
              {staff.map((row) => {
                const isEditing = editing === row.id;
                if (!isEditing) {
                  return (
                    <tr key={row.id}>
                      <td className="py-3 text-ink">{row.full_name}</td>
                      <td className="py-3 text-ink-dim">{row.role}</td>
                      <td className="py-3 text-ink-dim">{row.business_unit_name ?? "—"}</td>
                      <td className="py-3 text-ink-dim">{row.manager_name ?? "—"}</td>
                      <td className="py-3 text-ink-dim font-mono text-xs">
                        {row.timezone} · {row.daily_checkin_time}
                      </td>
                      <td className="py-3 text-ink-dim">
                        {row.status}
                        {row.opted_out === 1 ? " · opted out" : ""}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          type="button"
                          onClick={() => startEdit(row)}
                          className="text-xs text-accent-cyan hover:underline"
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  );
                }
                return (
                  <tr key={row.id} className="bg-bg-surface/40">
                    <td className="py-3">
                      <input
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.full_name ?? ""}
                        onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                      />
                    </td>
                    <td className="py-3">
                      <input
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.role ?? ""}
                        onChange={(e) => setDraft({ ...draft, role: e.target.value })}
                      />
                    </td>
                    <td className="py-3">
                      <select
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.business_unit_id ?? ""}
                        onChange={(e) =>
                          setDraft({
                            ...draft,
                            business_unit_id: e.target.value ? Number(e.target.value) : null,
                          })
                        }
                      >
                        <option value="">—</option>
                        {bus.map((bu) => (
                          <option key={bu.id} value={bu.id}>
                            {bu.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3">
                      <select
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.manager_id ?? ""}
                        onChange={(e) =>
                          setDraft({ ...draft, manager_id: e.target.value || null })
                        }
                      >
                        <option value="">—</option>
                        {(managers.length > 0 ? managers : staff).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.full_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 space-y-1">
                      <select
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.timezone}
                        onChange={(e) => setDraft({ ...draft, timezone: e.target.value })}
                      >
                        {TIMEZONES.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                      <input
                        type="time"
                        step={60}
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={(draft.daily_checkin_time ?? "09:00").slice(0, 5)}
                        onChange={(e) =>
                          setDraft({ ...draft, daily_checkin_time: `${e.target.value}:00` })
                        }
                      />
                    </td>
                    <td className="py-3 space-y-1">
                      <select
                        className="w-full bg-bg-base border border-bg-line2 rounded px-2 py-1 text-ink"
                        value={draft.status}
                        onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <label className="flex items-center gap-1 text-[11px] text-ink-dim">
                        <input
                          type="checkbox"
                          checked={draft.opted_out === 1}
                          onChange={(e) =>
                            setDraft({ ...draft, opted_out: e.target.checked ? 1 : 0 })
                          }
                        />
                        opted out
                      </label>
                      <label className="flex items-center gap-1 text-[11px] text-ink-dim">
                        <input
                          type="checkbox"
                          checked={draft.is_executive === 1}
                          onChange={(e) =>
                            setDraft({ ...draft, is_executive: e.target.checked ? 1 : 0 })
                          }
                        />
                        executive
                      </label>
                    </td>
                    <td className="py-3 text-right space-x-2">
                      <button
                        type="button"
                        onClick={save}
                        disabled={saving}
                        className="text-xs px-2 py-1 rounded bg-accent-cyan/20 text-accent-cyan border border-accent-cyan/40 disabled:opacity-50"
                      >
                        {saving ? "Saving…" : "Save"}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(null);
                          setDraft({});
                        }}
                        className="text-xs px-2 py-1 rounded border border-bg-line2 text-ink-dim"
                      >
                        Cancel
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Panel>
      </div>
    </>
  );
}
